import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SEO, getBreadcrumbSchema } from '@/hooks/useSEO';
import { useLanguage } from '@/hooks/useLanguage';
import { useCounties } from '@/hooks/usePostalAreas';
import { PostalAreaGrid } from '@/components/postal/PostalAreaGrid';
import { Skeleton } from '@/components/ui/skeleton';

export default function PostalCodesInfoPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { data: counties, isLoading } = useCounties();

  const facts = [
    { value: '~17 000', labelKey: 'information.postalCodes.facts.totalCodes' },
    { value: '1 743', labelKey: 'information.postalCodes.facts.totalCities' },
    { value: '10 500', labelKey: 'information.postalCodes.facts.deliverableCodes' },
  ];

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('information.postalCodes.title')}
        description={t('meta.postalCodes')}
        path="/information/postnummer"
        jsonLd={getBreadcrumbSchema([
          { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
          { name: t('common.information'), url: `https://postnummerservice.se/${lang}/information/postnummer` },
          { name: t('information.postalCodes.title'), url: `https://postnummerservice.se/${lang}/information/postnummer` },
        ])}
      />
      <div className="max-w-5xl mx-auto space-y-6 md:space-y-10">
        {/* Hero Section - compact */}
        <section className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-xl bg-secondary/10 mb-3 md:mb-4">
            <MapPin className="w-6 h-6 md:w-8 md:h-8 text-secondary" />
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-1.5 md:mb-2">
            {t('information.postalCodes.title')}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
            {t('information.postalCodes.subtitle')}
          </p>
        </section>

        {/* Quick Facts - compact row */}
        <section className="grid gap-2 md:gap-3 grid-cols-3">
          {facts.map((fact, index) => (
            <Card key={index} className="border-border/60">
              <CardContent className="p-2 md:p-4 text-center">
                <p className="text-base md:text-2xl font-bold text-secondary whitespace-nowrap">{fact.value}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5">{t(fact.labelKey)}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        {/* Counties List - now prominent */}
        <section>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20" />)}
              </div>
            </div>
          ) : counties && counties.length > 0 ? (
            <PostalAreaGrid
              areas={counties}
              buildHref={(c) => `/${lang}/information/postnummer/${c.slug ?? c.code}`}
              childLabel={t('postal.counties')}
            />
          ) : null}
        </section>
      </div>
    </div>
  );
}
